import {
  Box,
  Chip,
  Button,
  useTheme,
  TextField,
  FormLabel,
  DialogTitle,
  FormControl,
  Autocomplete,
  svgIconClasses,
  DialogContentText,
} from '@mui/material';
import * as yup from 'yup';
import { useState } from 'react';
import { useSnackbar } from 'notistack';
import apiService from 'library/api-service';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { gray } from 'providers/theme/themePrimitives';
import { EMAIL_ADDRESS_PATTERN, getFieldErrorMessages } from 'utils';
import { LoadingBackdrop, ResponsiveDialog, ResponsiveDialogActions, ResponsiveDialogContent } from 'components';

type CreateEffortContactDialogProps = {
  open: boolean;
  handleClose: () => void;
  refresh: () => Promise<void>;
};

export type CreateEffortContactFormParams = {
  title: string;
  emails: string[];
};

const CreateEffortContactDialog = ({ open, handleClose: closeDialog, refresh }: CreateEffortContactDialogProps) => {
  // other hooks
  const intl = useIntl();
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();

  // useState hooks
  const [loading, setLoading] = useState(false);

  // form validations & form state
  const schema = yup.object({
    title: yup
      .string()
      .trim()
      .required(intl.formatMessage({ id: 'validations.required', defaultMessage: 'Bu alan boş bırakılamaz' })),
    emails: yup
      .array()
      .of(
        yup
          .string()
          .trim()
          .required(intl.formatMessage({ id: 'validations.required', defaultMessage: 'Bu alan boş bırakılamaz' }))
          .matches(
            EMAIL_ADDRESS_PATTERN,
            intl.formatMessage({ id: 'validations.invalidEmailFormat', defaultMessage: 'Geçersiz e-posta formatı' })
          )
      )
      .min(
        1,
        intl.formatMessage({ id: 'validations.emails.minOne', defaultMessage: 'En az 1 e-posta adresi girilmelidir.' })
      )
      .test(
        'unique',
        intl.formatMessage({
          id: 'validations.emails.duplicate',
          defaultMessage: 'E-posta listesinde tekrar eden adresler var.',
        }),
        (arr) => {
          const list = (arr ?? []).map((x) => x.trim().toLowerCase());
          return new Set(list).size === list.length;
        }
      )
      .required(),
  });

  const { reset, control, handleSubmit } = useForm<CreateEffortContactFormParams>({
    defaultValues: { title: '', emails: [] },
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    // @ts-ignore
    resolver: yupResolver(schema),
    shouldFocusError: true,
  });

  // sync helper functions
  const handleClose = () => {
    closeDialog();
    reset();
  };

  // async helper functions
  const onSubmit = async (data: CreateEffortContactFormParams) => {
    try {
      setLoading(true);

      const response = await apiService.createEffortContact(data);

      if (response.isSuccess) {
        enqueueSnackbar(
          <FormattedMessage
            id="effortContacts.createEffortContactCompleted"
            defaultMessage="Efor Muhatabı oluşturma işlemi başarıyla tamamlandı."
          />,
          { variant: 'success' }
        );
        handleClose();
        refresh();
      }
    } catch {
    } finally {
      setLoading(false);
    }
  };

  return (
    <ResponsiveDialog disableRestoreFocus open={open} onClose={handleClose}>
      <DialogTitle>
        <FormattedMessage id="effortContacts.createEffortContact" defaultMessage="Efor Muhatabı Oluştur" />
      </DialogTitle>
      <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
        <ResponsiveDialogContent sx={{ display: 'grid' }}>
          <DialogContentText>
            <FormattedMessage
              id="effortContacts.createEffortContactText"
              defaultMessage="İlgili alanları girip devam ederek yeni bir Efor Muhatabı oluşturabilirsiniz."
            />
          </DialogContentText>
          <FormControl>
            <FormLabel htmlFor="title">
              <FormattedMessage id="gibTypes.title" defaultMessage="Adı" />
            </FormLabel>
            <Controller
              name="title"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField {...field} fullWidth autoFocus size="small" error={!!error} helperText={error?.message} />
              )}
            />
          </FormControl>
          <FormControl>
            <FormLabel htmlFor="emails">
              <FormattedMessage id="effortContacts.emails" defaultMessage="E-Postalar" />
            </FormLabel>
            <Controller
              name="emails"
              control={control}
              render={({ field, fieldState: { error } }) => {
                const errorMessage = getFieldErrorMessages(error);

                return (
                  <Autocomplete
                    size="small"
                    multiple
                    freeSolo
                    options={[]}
                    value={field.value ?? []}
                    onChange={(_, newValue) => {
                      const normalized = (newValue as string[])
                        .map((x) => (typeof x === 'string' ? x : String(x)))
                        .map((x) => x.trim())
                        .filter(Boolean);

                      field.onChange(normalized);
                    }}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          variant="outlined"
                          label={option}
                          {...getTagProps({ index })}
                          key={`${option}-${index}`}
                        />
                      ))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        sx={{
                          '& .MuiButtonBase-root': {
                            border: 'none',
                            width: 'fit-content',
                            height: '2.25rem',
                            padding: '0.25rem',
                            [`& .${svgIconClasses.root}`]: { fontSize: '1rem' },
                            '&:hover': {
                              backgroundColor: theme.palette.mode === 'light' ? gray[100] : gray[800],
                            },
                          },
                          '& .MuiInputBase-root': {
                            height: 'auto',
                          },
                        }}
                        placeholder={intl.formatMessage({
                          id: 'effortContacts.pushEnterAfterWritingEmail',
                          defaultMessage: 'E-Posta yazıp Enter’a basın',
                        })}
                        error={!!error}
                        helperText={errorMessage}
                      />
                    )}
                  />
                );
              }}
            />
          </FormControl>
        </ResponsiveDialogContent>
        <ResponsiveDialogActions>
          <Button onClick={handleClose}>
            <FormattedMessage id="forgotPassword.cancel" defaultMessage="Vazgeç" />
          </Button>
          <Button variant="contained" type="submit">
            <FormattedMessage id="forgotPassword.continue" defaultMessage="Devam" />
          </Button>
        </ResponsiveDialogActions>
      </Box>
      <LoadingBackdrop open={loading} />
    </ResponsiveDialog>
  );
};

export default CreateEffortContactDialog;
